import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Stack, Tooltip, IconButton, Box } from "@mui/material";

import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockIcon from "@mui/icons-material/Lock";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import DateRangeFilter from "../components/common/DateRangeFilter";
import AppButton from "../components/buttons/AppButton";
import StandardTable, { TableColumn } from "../components/common/StandardTable";

import OpenShiftModal from "../components/shifts/OpenShiftModal";
import CloseShiftModal from "../components/shifts/CloseShiftModal";
import ViewShiftModal from "../components/shifts/ViewShiftModal";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getShifts,
  getShiftById,
  getCurrentOrRecentShift,
  checkCashierHasOpenShift,
  resetShiftServerStatus,
} from "../redux/actions/shiftActions";
import StatusChip from "../components/common/StatusChip";
import { ShiftSummaryDto } from "../shared/dtos/shift/ShiftSummaryDto";
import { formatPlainNumber } from "../utils/appUtils";

const ShiftsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const today = new Date().toISOString().split("T")[0];

  // --- Redux State Mapping ---
  const {
    shifts,
    pagination,
    loadingFetchAll: loadingShifts,
    errorFetchAll: errorLoadingShifts,
    currentShift,
    loadingFetchCurrent: loadingShift,
    loadingOpen: loadingShiftOpen,
    loadingClose: loadingShiftClose,
    errorOpen: errorOpeningShift,
    errorClose: errorClosingShift,
    hasOpenShift,
    serverStatus,
  } = useAppSelector((state) => state.shift);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);
  const [startDate, setStartDate] = useState<string>(today);
  const [endDate, setEndDate] = useState<string>(today);
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });

  const [isOpenModalOpen, setIsOpenModalOpen] = useState<boolean>(false);
  const [isCloseModalOpen, setIsCloseModalOpen] = useState<boolean>(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);

  const canCreate = hasPermission("shift:create");
  const canUpdate = hasPermission("shift:update");
  const canRead = hasPermission("shift:read_all");

  const fetchShiftsList = useCallback(() => {
    if (!canRead) return;
    dispatch(
      getShifts({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch,
        startDate,
        endDate,
      }),
    );
  }, [
    dispatch,
    pageConfig.page,
    pageConfig.limit,
    debouncedSearch,
    startDate,
    endDate,
    canRead,
  ]);

  useEffect(() => {
    fetchShiftsList();
  }, [fetchShiftsList]);

  useEffect(() => {
    if (canCreate) dispatch(checkCashierHasOpenShift());
  }, [dispatch, canCreate]);

  // Modal handlers
  const handleOpenShiftClick = (): void => setIsOpenModalOpen(true);
  const handleCloseOpenModal = useCallback(() => {
    if (!loadingShiftOpen) setIsOpenModalOpen(false);
  }, [loadingShiftOpen]);

  const handleCloseShiftClick = (): void => {
    dispatch(getCurrentOrRecentShift());
    setIsCloseModalOpen(true);
  };
  const handleCloseCloseModal = useCallback(() => {
    if (!loadingShiftClose) setIsCloseModalOpen(false);
  }, [loadingShiftClose]);

  const handleViewShift = (id: string): void => {
    dispatch(getShiftById(id));
    setIsViewModalOpen(true);
  };

  useEffect(() => {
    if (!serverStatus) return;
    if (serverStatus.operation === "open" && serverStatus.status === 201)
      handleCloseOpenModal();
    if (serverStatus.operation === "close" && serverStatus.status === 200)
      handleCloseCloseModal();
    dispatch(resetShiftServerStatus());
  }, [serverStatus, dispatch, handleCloseOpenModal, handleCloseCloseModal]);

  const columns: TableColumn<ShiftSummaryDto>[] = useMemo(
    () => [
      // --- DEFAULT VISIBLE COLUMNS ---
      {
        id: "cashier",
        label: "Cashier",
        minWidth: 120,
        format: (_, row) => (
          <Box component="span" sx={{ fontWeight: 600 }}>
            {row.cashier}
          </Box>
        ),
      },
      { id: "branchName", label: "Branch", minWidth: 120 },
      {
        id: "startTime",
        label: "Opened At",
        minWidth: 150,
        format: (_, row) => new Date(row.startTime).toLocaleString(),
      },
      {
        id: "endTime",
        label: "Closed At",
        minWidth: 150,
        format: (_, row) =>
          row.endTime ? new Date(row.endTime).toLocaleString() : "-",
      },
      {
        id: "isOpen",
        label: "Status",
        minWidth: 100,
        format: (_, row) => (
          <StatusChip status={row.isOpen ? "OPEN" : "CLOSED"} />
        ),
      },
      {
        id: "openingFloat",
        label: "Opening Float",
        minWidth: 120,
        align: "right",
        format: (_, row) => formatPlainNumber(row.openingFloat),
      },
      {
        id: "expectedCash",
        label: "Expected Cash",
        minWidth: 120,
        align: "right",
        format: (_, row) => formatPlainNumber(row.expectedCash),
      },
      {
        id: "cashDiscrepancy",
        label: "Discrepancy",
        minWidth: 120,
        align: "right",
        format: (_, row) =>
          row.isOpen || row.cashDiscrepancy === undefined ? (
            "-"
          ) : (
            <Box
              component="span"
              sx={{
                color:
                  row.cashDiscrepancy < 0
                    ? "error.main"
                    : row.cashDiscrepancy > 0
                      ? "warning.main"
                      : "success.main",
                fontWeight: "bold",
              }}
            >
              {formatPlainNumber(row.cashDiscrepancy)}
            </Box>
          ),
      },
    ],
    [],
  );

  const renderRowActions = (row: ShiftSummaryDto): React.JSX.Element => (
    <Stack direction="row" spacing={1} sx={{ justifyContent: "center" }}>
      <Tooltip title="View Z-Report">
        <IconButton
          size="small"
          color="info"
          onClick={() => handleViewShift(row.id)}
        >
          <VisibilityOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  return (
    <PageContainer
      title="POS Shifts & Registers"
      subtitle="Manage cashier sessions, daily floats, and Z-Reports."
      actions={
        <Stack direction="row" spacing={2}>
          {canCreate && !hasOpenShift && (
            <AppButton
              variant="contained"
              startIcon={<LockOpenIcon />}
              onClick={handleOpenShiftClick}
            >
              Open Shift
            </AppButton>
          )}
          {canUpdate && hasOpenShift && (
            <AppButton
              variant="contained"
              color="error"
              startIcon={<LockIcon />}
              onClick={handleCloseShiftClick}
            >
              Current Shift / Close
            </AppButton>
          )}
        </Stack>
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => {
          setSearchTerm(e.target.value);
          setPageConfig((p) => ({ ...p, page: 1 }));
        }}
        searchPlaceholder="Search by cashier or branch..."
      >
        <DateRangeFilter
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </PageToolbar>

      <StandardTable
        columns={columns}
        data={shifts}
        loading={loadingShifts}
        error={errorLoadingShifts}
        onRetry={fetchShiftsList}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(p) => setPageConfig({ ...pageConfig, page: p })}
        onRowsPerPageChange={(l) =>
          setPageConfig({ ...pageConfig, limit: l, page: 1 })
        }
        enableColumnManagement={true}
        rowAction={renderRowActions}
      />

      {isOpenModalOpen && (
        <OpenShiftModal
          open={isOpenModalOpen}
          onClose={handleCloseOpenModal}
          loading={loadingShiftOpen}
          error={errorOpeningShift}
        />
      )}

      {isCloseModalOpen && (
        <CloseShiftModal
          open={isCloseModalOpen}
          onClose={handleCloseCloseModal}
          currentShift={currentShift}
          loadingData={loadingShift}
          loadingClose={loadingShiftClose}
          error={errorClosingShift}
        />
      )}

      {isViewModalOpen && (
        <ViewShiftModal
          open={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          shift={currentShift}
          loading={loadingShift}
          error={null}
        />
      )}
    </PageContainer>
  );
};

export default ShiftsPage;
